/**
 * S3 Key patterns and generators for consistent file organization
 */

// S3 Key prefixes
export const S3_KEY_PREFIXES = {
    LOGO: 'logo',
    CV: 'cv',
    STATIC_LOGO: 'static/logo',
    PROFILE_PICTURES: 'static/profilepictures',
} as const;

// Logo-related key generators
export const LogoKeys = {
    /**
     * Generate upload key for company logo
     * @param companyId Company identifier
     * @param themeMode Theme mode (light/dark)
     * @param fileId Unique file identifier
     * @returns S3 key for logo upload
     */
    upload: (companyId: string, themeMode: 'light' | 'dark', fileId: string): string =>
        `${S3_KEY_PREFIXES.LOGO}/${companyId}/${themeMode}/${fileId}.png`,

    /**
     * Generate static key for processed company logo
     * @param companyId Company identifier
     * @param size Image size
     * @param logoId Logo identifier
     * @returns S3 key for processed logo
     */
    static: (companyId: string, size: number, logoId: string): string =>
        `${S3_KEY_PREFIXES.STATIC_LOGO}/${companyId}/${size}-${logoId}.png`,

    /**
     * Generate regex pattern for logo upload keys
     * @returns Regex pattern to match logo upload keys
     */
    uploadPattern: (): RegExp => /logo\/(.*?)\/(light|dark)\/(.*?)\.png/,
} as const;

// CV-related key generators
export const CVKeys = {
    /**
     * Generate key for CV upload
     * @param companyId Company identifier
     * @param fileId Unique file identifier
     * @returns S3 key for CV upload
     */
    upload: (companyId: string, fileId: string): string => `${S3_KEY_PREFIXES.CV}/${companyId}/${fileId}.docx`,
} as const;

// Profile picture key generators
export const ProfilePictureKeys = {
    /**
     * Generate key for profile picture
     * @param companyId Company identifier
     * @param employeeId Employee identifier
     * @param size Image size
     * @param pictureId Picture identifier
     * @returns S3 key for profile picture
     */
    static: (companyId: string, employeeId: number, size: number, pictureId: string): string =>
        `${S3_KEY_PREFIXES.PROFILE_PICTURES}/${companyId}/${employeeId}-${size}-${pictureId}.png`,
} as const;
